// Editor sections that exist in EVERY theme — the chrome above and below the
// homepage blocks, plus the store-wide settings that have no single place on
// the page. The per-theme homepage blocks live in editor-sections-<theme>.ts
// and are spliced between `HEADER_SECTIONS` and `FOOTER_SECTIONS` by
// `getEditorSections` (editor-sections.ts).

import type { EditorFieldDef, EditorMediaSpec, EditorSectionDef } from './editor-schema';
import {
  ARABIC_FONT_OPTIONS,
  BORDER_RADIUS_OPTIONS,
  HEADING_SCALE_OPTIONS,
  LATIN_FONT_OPTIONS,
  SHADOW_STYLE_OPTIONS,
} from './editor-schema';

const fontOptions = (values: readonly string[]) =>
  values.map((value) => ({ value, label: { fr: value, ar: value, en: value } }));

// ---------------------------------------------------------------- Logo exports
//
// Every logo slot below is painted with `object-contain`, so the file is fitted
// WHOLE into its box and is never cropped. The ratio each spec carries is the
// shape of the box — a logo squarer than that simply leaves side margin — and
// the pixel size is twice the box so it stays sharp on a retina screen.

/**
 * `logoHorizontalUrl` is surfaced by TWO fields (the header chrome and the
 * identity panel) but is a single stored file, so both quote this one spec —
 * two hand-written copies would eventually tell the merchant two sizes for the
 * same upload.
 *
 * Largest box on a wide desktop = the default header's `md:h-11 max-w-[180px]`
 * (apps/storefront/src/components/Header.tsx) → 180 × 44 CSS px. The other
 * themes are smaller (Elessi `h-9 max-w-[170px]`, Pebble `h-8 lg:h-9
 * max-w-[170px]`), and every footer paints it at h-9/h-10. ×2 → 360 × 88.
 */
const HORIZONTAL_LOGO_MEDIA: EditorMediaSpec = {
  width: 360,
  height: 88,
  fit: 'contain',
  // PNG only, deliberately: SVG is refused by BOTH uploaders — the storefront's
  // mime allowlist omits it (ImageUploadField) and the dashboard sniffs the
  // magic bytes and fails with `svg_not_allowed` (apps/erp-web/src/lib/
  // image-validation.ts), because an SVG is a script-carrying document. Naming
  // it here would send merchants straight into "Format non pris en charge".
  note: {
    fr: "Fond transparent (PNG) : le pied de page repeint le logo en blanc, un fond opaque y deviendrait un rectangle. Le logo est affiché en entier, jamais recadré — plus étroit, il tient sans problème.",
    ar: 'خلفية شفافة (PNG): التذييل يعيد تلوين الشعار بالأبيض، والخلفية غير الشفافة تتحوّل إلى مستطيل. يظهر الشعار كاملًا دون اقتصاص — وإن كان أضيق فلا مشكلة.',
    en: 'Transparent background (PNG): the footer repaints the logo in white, so an opaque background turns into a rectangle. The logo is shown whole, never cropped — a narrower one fits fine.',
  },
};

// ---------------------------------------------------------------- Header chrome

export const ANNOUNCEMENT_SECTION: EditorSectionDef = {
  key: 'announcement',
  group: 'header',
  pinned: true,
  icon: 'announcement',
  label: { fr: "Bandeau d'annonce", ar: 'شريط الإعلان', en: 'Announcement bar' },
  description: { fr: 'La bande fine tout en haut', ar: 'الشريط العلوي الرفيع', en: 'The thin strip at the very top' },
  visibilityField: { surface: 'settings', key: 'announcementBarEnabled' },
  fields: [
    {
      path: 'announcement.text',
      kind: 'localized',
      surface: 'settings',
      key: 'announcementBarText',
      label: { fr: "Texte de l'annonce", ar: 'نص الإعلان', en: 'Announcement text' },
      hint: {
        fr: 'Séparez plusieurs messages par une barre verticale « | » — ils défileront à tour de rôle.',
        ar: 'افصل بين الرسائل بعلامة « | » لتظهر بالتناوب.',
        en: 'Separate several messages with a pipe “|” — they rotate one after another.',
      },
    },
  ],
};

export const HEADER_SECTION: EditorSectionDef = {
  key: 'header',
  group: 'header',
  pinned: true,
  icon: 'header',
  label: { fr: 'En-tête', ar: 'الترويسة', en: 'Header' },
  description: { fr: 'Logo, recherche, panier, langues', ar: 'الشعار، البحث، السلة، اللغات', en: 'Logo, search, cart, languages' },
  fields: [
    {
      path: 'header.storeName',
      kind: 'text',
      surface: 'settings',
      key: 'storeName',
      label: { fr: 'Nom de la boutique', ar: 'اسم المتجر', en: 'Store name' },
      hint: {
        fr: "Affiché à la place du logo quand aucun logo n'est chargé.",
        ar: 'يظهر بدل الشعار عند عدم رفع شعار.',
        en: 'Shown instead of the logo when no logo is uploaded.',
      },
    },
    {
      path: 'header.logoHorizontal',
      kind: 'image',
      surface: 'theme',
      key: 'logoHorizontalUrl',
      label: { fr: 'Logo horizontal', ar: 'الشعار الأفقي', en: 'Horizontal logo' },
      hint: { fr: "Le logo affiché dans l'en-tête.", ar: 'الشعار الظاهر في الترويسة.', en: 'The logo shown in the header.' },
      media: HORIZONTAL_LOGO_MEDIA,
    },
    {
      path: 'header.stickyHeader',
      kind: 'toggle',
      surface: 'theme',
      key: 'stickyHeader',
      label: { fr: 'En-tête fixe au défilement', ar: 'ترويسة ثابتة عند التمرير', en: 'Sticky header on scroll' },
    },
    {
      path: 'header.showSearchBar',
      kind: 'toggle',
      surface: 'theme',
      key: 'showSearchBar',
      label: { fr: 'Barre de recherche', ar: 'شريط البحث', en: 'Search bar' },
    },
    {
      path: 'header.showCartBadge',
      kind: 'toggle',
      surface: 'theme',
      key: 'showCartBadge',
      label: { fr: 'Pastille du panier', ar: 'شارة السلة', en: 'Cart badge' },
    },
    {
      path: 'header.showLanguageSwitcher',
      kind: 'toggle',
      surface: 'theme',
      key: 'showLanguageSwitcher',
      label: { fr: 'Sélecteur de langue', ar: 'مبدّل اللغة', en: 'Language switcher' },
    },
    {
      path: 'header.phone',
      kind: 'text',
      surface: 'settings',
      key: 'phone',
      label: { fr: 'Téléphone affiché', ar: 'الهاتف المعروض', en: 'Displayed phone' },
      hint: {
        fr: 'Visible dans la barre supérieure et le pied de page.',
        ar: 'يظهر في الشريط العلوي وأسفل الصفحة.',
        en: 'Shown in the top bar and the footer.',
      },
    },
  ],
};

export const HEADER_SECTIONS: EditorSectionDef[] = [ANNOUNCEMENT_SECTION, HEADER_SECTION];

// ---------------------------------------------------------------- Footer chrome

export const FOOTER_SECTION: EditorSectionDef = {
  key: 'footer',
  group: 'footer',
  pinned: true,
  icon: 'footer',
  label: { fr: 'Pied de page', ar: 'تذييل الصفحة', en: 'Footer' },
  description: { fr: 'Colonnes, mentions et paiements', ar: 'الأعمدة والحقوق ووسائل الدفع', en: 'Columns, legal and payments' },
  fields: [
    {
      path: 'footer.tagline',
      kind: 'localizedMultiline',
      surface: 'settings',
      key: 'footerTagline',
      label: { fr: 'Phrase de présentation', ar: 'جملة التعريف', en: 'Tagline' },
      hint: {
        fr: 'Le court paragraphe sous le logo. Vide = la description SEO est utilisée.',
        ar: 'الفقرة القصيرة تحت الشعار. اتركها فارغة لاستعمال وصف السيو.',
        en: 'The short paragraph under the logo. Empty = the SEO description is used.',
      },
      fallsBackToDefault: true,
    },
    {
      path: 'footer.copyright',
      kind: 'text',
      surface: 'settings',
      key: 'footerCopyright',
      label: { fr: 'Mention de copyright', ar: 'حقوق النشر', en: 'Copyright line' },
      hint: {
        fr: "Vide = « © année Nom de la boutique. Tous droits réservés. »",
        ar: 'اتركها فارغة لاستعمال الصيغة الافتراضية.',
        en: 'Empty = “© year Store name. All rights reserved.”',
      },
      fallsBackToDefault: true,
    },
    {
      path: 'footer.paymentMethods',
      kind: 'text',
      surface: 'settings',
      key: 'footerPaymentMethods',
      label: { fr: 'Moyens de paiement', ar: 'وسائل الدفع', en: 'Payment methods' },
      hint: {
        fr: 'Séparés par des virgules. Ex. : Cash, Visa, Mastercard, e-Dinar.',
        ar: 'مفصولة بفواصل. مثال: نقدًا، فيزا، ماستركارد، الدينار الإلكتروني.',
        en: 'Comma separated. E.g. Cash, Visa, Mastercard, e-Dinar.',
      },
      fallsBackToDefault: true,
    },
    {
      path: 'footer.businessAddress',
      kind: 'textarea',
      surface: 'settings',
      key: 'businessAddress',
      label: { fr: 'Adresse affichée', ar: 'العنوان المعروض', en: 'Displayed address' },
    },
    {
      path: 'footer.email',
      kind: 'text',
      surface: 'settings',
      key: 'email',
      label: { fr: 'E-mail de contact', ar: 'بريد التواصل', en: 'Contact email' },
    },
    {
      path: 'footer.backgroundColor',
      kind: 'color',
      surface: 'theme',
      key: 'footerBackgroundColor',
      label: { fr: 'Couleur de fond', ar: 'لون الخلفية', en: 'Background colour' },
      hint: {
        fr: 'Vide = la couleur primaire de la marque.',
        ar: 'اتركه فارغًا لاستعمال اللون الأساسي.',
        en: 'Empty = the brand primary colour.',
      },
      fallsBackToDefault: true,
    },
    {
      path: 'footer.textColor',
      kind: 'color',
      surface: 'theme',
      key: 'footerTextColor',
      label: { fr: 'Couleur du texte', ar: 'لون النص', en: 'Text colour' },
      hint: {
        fr: 'Vide = noir ou blanc choisi automatiquement pour rester lisible.',
        ar: 'اتركه فارغًا ليُختار تلقائيًا أبيض أو أسود حسب الوضوح.',
        en: 'Empty = black or white picked automatically so the text stays legible.',
      },
      fallsBackToDefault: true,
    },
  ],
};

export const SOCIAL_SECTION: EditorSectionDef = {
  key: 'social',
  group: 'footer',
  pinned: true,
  icon: 'social',
  label: { fr: 'Réseaux sociaux', ar: 'شبكات التواصل', en: 'Social links' },
  description: { fr: 'Liens affichés dans le pied de page', ar: 'روابط تظهر في التذييل', en: 'Links shown in the footer' },
  fields: [
    { path: 'social.facebook', kind: 'text', surface: 'settings', key: 'socialFacebook', label: { fr: 'Facebook', ar: 'فيسبوك', en: 'Facebook' } },
    { path: 'social.instagram', kind: 'text', surface: 'settings', key: 'socialInstagram', label: { fr: 'Instagram', ar: 'إنستغرام', en: 'Instagram' } },
    { path: 'social.tiktok', kind: 'text', surface: 'settings', key: 'socialTiktok', label: { fr: 'TikTok', ar: 'تيك توك', en: 'TikTok' } },
  ],
};

export const WHATSAPP_SECTION: EditorSectionDef = {
  key: 'whatsapp',
  group: 'footer',
  pinned: true,
  icon: 'whatsapp',
  label: { fr: 'Bouton WhatsApp', ar: 'زر واتساب', en: 'WhatsApp button' },
  description: { fr: 'La pastille flottante de contact', ar: 'زر التواصل العائم', en: 'The floating contact bubble' },
  visibilityField: { surface: 'settings', key: 'whatsappEnabled' },
  fields: [
    {
      path: 'whatsapp.number',
      kind: 'text',
      surface: 'settings',
      key: 'whatsappNumber',
      label: { fr: 'Numéro WhatsApp', ar: 'رقم واتساب', en: 'WhatsApp number' },
      hint: { fr: 'Au format international, ex. +216…', ar: 'بالصيغة الدولية، مثال +216…', en: 'International format, e.g. +216…' },
    },
    {
      path: 'whatsapp.position',
      kind: 'select',
      surface: 'settings',
      key: 'whatsappPosition',
      label: { fr: 'Position', ar: 'الموضع', en: 'Position' },
      options: [
        { value: 'left', label: { fr: 'À gauche', ar: 'إلى اليسار', en: 'Left' } },
        { value: 'right', label: { fr: 'À droite', ar: 'إلى اليمين', en: 'Right' } },
      ],
    },
    {
      path: 'whatsapp.animation',
      kind: 'select',
      surface: 'settings',
      key: 'whatsappAnimation',
      label: { fr: 'Animation', ar: 'الحركة', en: 'Animation' },
      options: [
        { value: 'none', label: { fr: 'Aucune', ar: 'بدون', en: 'None' } },
        { value: 'shake', label: { fr: 'Secousse', ar: 'اهتزاز', en: 'Shake' } },
        { value: 'pulse', label: { fr: 'Pulsation', ar: 'نبض', en: 'Pulse' } },
        { value: 'bounce', label: { fr: 'Rebond', ar: 'ارتداد', en: 'Bounce' } },
      ],
    },
  ],
};

export const FOOTER_SECTIONS: EditorSectionDef[] = [FOOTER_SECTION, SOCIAL_SECTION, WHATSAPP_SECTION];

// ---------------------------------------------------------------- Global settings

const BRAND_COLOR_FIELDS: EditorFieldDef[] = [
  {
    path: 'brandColors.primary',
    kind: 'color',
    surface: 'theme',
    key: 'primaryColor',
    label: { fr: 'Primaire', ar: 'الأساسي', en: 'Primary' },
    hint: { fr: 'Titres, en-tête et pied de page.', ar: 'العناوين والترويسة والتذييل.', en: 'Headings, header and footer.' },
  },
  {
    path: 'brandColors.secondary',
    kind: 'color',
    surface: 'theme',
    key: 'secondaryColor',
    label: { fr: 'Secondaire', ar: 'الثانوي', en: 'Secondary' },
    hint: {
      fr: 'Liens et accents — pilote aussi le surlignage du thème Pebble.',
      ar: 'الروابط واللمسات — ويحدّد أيضًا تظليل قالب Pebble.',
      en: 'Links and accents — also drives the Pebble highlight.',
    },
  },
  {
    path: 'brandColors.tertiary',
    kind: 'color',
    surface: 'theme',
    key: 'tertiaryColor',
    label: { fr: 'Tertiaire', ar: 'الثالثي', en: 'Tertiary' },
    hint: {
      fr: 'Fonds pastel des sections décoratives.',
      ar: 'خلفيات باستيل للأقسام الزخرفية.',
      en: 'Pastel backgrounds of the decorative sections.',
    },
  },
  {
    path: 'brandColors.background',
    kind: 'color',
    surface: 'theme',
    key: 'backgroundColor',
    label: { fr: 'Fond des pages', ar: 'خلفية الصفحات', en: 'Page background' },
  },
  {
    path: 'brandColors.text',
    kind: 'color',
    surface: 'theme',
    key: 'textColor',
    label: { fr: 'Texte courant', ar: 'نص المحتوى', en: 'Body text' },
  },
  {
    path: 'brandColors.sale',
    kind: 'color',
    surface: 'theme',
    key: 'saleColor',
    label: { fr: 'Promotions', ar: 'التخفيضات', en: 'Sale' },
    hint: { fr: 'Prix barrés et badges de réduction.', ar: 'الأسعار المشطوبة وشارات التخفيض.', en: 'Struck prices and discount badges.' },
    fallsBackToDefault: true,
  },
  {
    path: 'brandColors.ctaButton',
    kind: 'color',
    surface: 'theme',
    key: 'ctaButtonColor',
    label: { fr: "Bouton d'achat", ar: 'زر الشراء', en: 'Buy button' },
  },
  {
    path: 'brandColors.ctaText',
    kind: 'color',
    surface: 'theme',
    key: 'ctaTextColor',
    label: { fr: "Texte du bouton d'achat", ar: 'نص زر الشراء', en: 'Buy button text' },
  },
];

export const BRAND_COLORS_SECTION: EditorSectionDef = {
  key: 'brandColors',
  group: 'global',
  pinned: true,
  icon: 'palette',
  label: { fr: 'Couleurs de la marque', ar: 'ألوان العلامة', en: 'Brand colours' },
  description: { fr: 'Une palette pour toute la boutique', ar: 'لوحة واحدة لكل المتجر', en: 'One palette for the whole store' },
  fields: BRAND_COLOR_FIELDS,
};

export const TYPOGRAPHY_SECTION: EditorSectionDef = {
  key: 'typography',
  group: 'global',
  pinned: true,
  icon: 'type',
  label: { fr: 'Typographie & style', ar: 'الخطوط والأسلوب', en: 'Typography & style' },
  description: { fr: 'Polices, arrondis et ombres', ar: 'الخطوط والانحناءات والظلال', en: 'Fonts, corners and shadows' },
  fields: [
    {
      path: 'typography.fontFamily',
      kind: 'select',
      surface: 'theme',
      key: 'fontFamily',
      label: { fr: 'Police latine', ar: 'الخط اللاتيني', en: 'Latin font' },
      options: fontOptions(LATIN_FONT_OPTIONS),
    },
    {
      path: 'typography.fontFamilyAr',
      kind: 'select',
      surface: 'theme',
      key: 'fontFamilyAr',
      label: { fr: 'Police arabe', ar: 'الخط العربي', en: 'Arabic font' },
      options: fontOptions(ARABIC_FONT_OPTIONS),
    },
    {
      path: 'typography.headingScale',
      kind: 'select',
      surface: 'theme',
      key: 'headingScale',
      label: { fr: 'Échelle des titres', ar: 'حجم العناوين', en: 'Heading scale' },
      options: HEADING_SCALE_OPTIONS,
    },
    {
      path: 'typography.borderRadius',
      kind: 'select',
      surface: 'theme',
      key: 'borderRadius',
      label: { fr: 'Arrondi des coins', ar: 'انحناء الزوايا', en: 'Corner radius' },
      options: BORDER_RADIUS_OPTIONS,
    },
    {
      path: 'typography.shadowStyle',
      kind: 'select',
      surface: 'theme',
      key: 'shadowStyle',
      label: { fr: 'Ombres', ar: 'الظلال', en: 'Shadows' },
      options: SHADOW_STYLE_OPTIONS,
    },
  ],
};

export const BRAND_SECTION: EditorSectionDef = {
  key: 'brand',
  group: 'global',
  pinned: true,
  icon: 'brand',
  label: { fr: 'Identité & logos', ar: 'الهوية والشعارات', en: 'Identity & logos' },
  description: { fr: 'Nom, logos, favicon et SEO', ar: 'الاسم والشعارات والأيقونة والسيو', en: 'Name, logos, favicon and SEO' },
  fields: [
    { path: 'brand.storeName', kind: 'text', surface: 'settings', key: 'storeName', label: { fr: 'Nom de la boutique', ar: 'اسم المتجر', en: 'Store name' } },
    { path: 'brand.legalName', kind: 'text', surface: 'settings', key: 'legalName', label: { fr: 'Raison sociale', ar: 'الاسم القانوني', en: 'Legal name' } },
    {
      path: 'brand.logoSquare',
      kind: 'image',
      surface: 'theme',
      key: 'logoSquareUrl',
      label: { fr: 'Logo carré', ar: 'شعار مربع', en: 'Square logo' },
      hint: { fr: 'Utilisé pour les partages et les aperçus.', ar: 'يُستعمل في المشاركات والمعاينات.', en: 'Used for sharing and previews.' },
      // This slot has no big box of its own on the storefront — it is the
      // `og:image` and the schema.org `logo` (resolveLogo in
      // apps/storefront/functions/_lib/seo.ts reads logo_square_url), plus a
      // last-resort fallback for the header/footer logo. Its largest render is
      // therefore someone else's: a Facebook / Twitter link card is ~500 CSS px
      // wide, so ×2 → 1000, rounded up to the usual square asset size.
      media: {
        width: 1024,
        height: 1024,
        fit: 'contain',
        note: {
          fr: "C'est l'image qui part avec votre boutique quand un lien est partagé sur Facebook ou WhatsApp : carrée, logo bien centré, avec un peu de marge autour car les réseaux rognent les bords.",
          ar: 'هذه هي الصورة التي تُرافق متجرك عند مشاركة رابط على فيسبوك أو واتساب: مربّعة، والشعار في الوسط مع هامش حوله لأن الشبكات تقتطع الأطراف.',
          en: 'This is the picture that travels with your store when a link is shared on Facebook or WhatsApp: square, logo well centred, with some margin around it because the networks trim the edges.',
        },
      },
    },
    {
      path: 'brand.logoHorizontal',
      kind: 'image',
      surface: 'theme',
      key: 'logoHorizontalUrl',
      label: { fr: 'Logo horizontal', ar: 'شعار أفقي', en: 'Horizontal logo' },
      // Same stored file as `header.logoHorizontal` — same spec, by reference.
      media: HORIZONTAL_LOGO_MEDIA,
    },
    {
      path: 'brand.favicon',
      kind: 'image',
      surface: 'theme',
      key: 'faviconUrl',
      label: { fr: 'Favicon', ar: 'أيقونة الموقع', en: 'Favicon' },
      hint: { fr: "L'icône de l'onglet du navigateur (image carrée).", ar: 'أيقونة تبويب المتصفح (صورة مربعة).', en: 'The browser tab icon (square image).' },
      // Emitted as a bare `<link rel="icon">` with no `sizes` (theme-provider.tsx
      // and functions/_middleware.ts), so ONE file has to serve every size the
      // browser asks for: 16 and 32 px in the tab, 180 px for an iOS home-screen
      // shortcut, 512 px for an Android one. The export is set by that largest
      // consumer, not by a CSS box — everything else is downscaled from it.
      media: {
        width: 512,
        height: 512,
        fit: 'contain',
        note: {
          fr: "Affichée à 16–32 px dans l'onglet du navigateur : mettez un symbole ou une initiale, pas le logo complet avec son texte — il deviendrait illisible. Fond transparent.",
          ar: 'تظهر بحجم 16–32 بكسل في تبويب المتصفح: استعمل رمزًا أو حرفًا أوّل، لا الشعار الكامل بنصّه لأنه يصبح غير مقروء. خلفية شفافة.',
          en: 'Shown at 16–32 px in the browser tab: use a symbol or a single letter, not the full logo with its wording — it would be unreadable. Transparent background.',
        },
      },
    },
    {
      path: 'brand.loginLogo',
      kind: 'image',
      surface: 'theme',
      key: 'loginLogoUrl',
      label: { fr: 'Logo de connexion', ar: 'شعار تسجيل الدخول', en: 'Login logo' },
      // Largest box = the Espace Pro sign-in card, `max-h-16 max-w-[220px]`
      // (apps/erp-pro/src/components/layout/AuthLayout.tsx) → 220 × 64 CSS px.
      // The same file also falls back into the Pro sidebar (`max-h-10
      // max-w-[160px]`) and, further down the chain, into the storefront header.
      // ×2 → 440 × 128.
      media: {
        width: 440,
        height: 128,
        fit: 'contain',
        note: {
          fr: "Le logo en haut de la page de connexion à l'Espace Pro, sur fond clair. Fond transparent, et il est affiché en entier — un logo plus étroit laisse simplement de la marge.",
          ar: 'الشعار أعلى صفحة تسجيل الدخول إلى فضاء المحترفين، على خلفية فاتحة. اجعل خلفيته شفافة؛ يظهر كاملًا، والشعار الأضيق يترك هامشًا فقط.',
          en: 'The logo at the top of the Espace Pro sign-in page, on a light background. Keep it transparent; it is shown whole, and a narrower logo just leaves margin.',
        },
      },
    },
    { path: 'brand.email', kind: 'text', surface: 'settings', key: 'email', label: { fr: 'E-mail', ar: 'البريد الإلكتروني', en: 'Email' } },
    { path: 'brand.phone', kind: 'text', surface: 'settings', key: 'phone', label: { fr: 'Téléphone', ar: 'الهاتف', en: 'Phone' } },
    { path: 'brand.businessAddress', kind: 'textarea', surface: 'settings', key: 'businessAddress', label: { fr: 'Adresse', ar: 'العنوان', en: 'Address' } },
    {
      path: 'brand.seoDescription',
      kind: 'localizedMultiline',
      surface: 'settings',
      key: 'seoDescription',
      label: { fr: 'Description SEO', ar: 'وصف السيو', en: 'SEO description' },
      hint: {
        fr: 'Le texte affiché par Google sous le nom de la boutique.',
        ar: 'النص الذي يعرضه غوغل تحت اسم المتجر.',
        en: 'The text Google shows under the store name.',
      },
    },
  ],
};

export const BUY_BUTTON_SECTION: EditorSectionDef = {
  key: 'buyButton',
  group: 'global',
  pinned: true,
  icon: 'tag',
  label: { fr: "Bouton d'achat", ar: 'زر الشراء', en: 'Buy button' },
  description: { fr: 'Texte et comportement sur la fiche produit', ar: 'النص والسلوك في صفحة المنتج', en: 'Text and behaviour on the product page' },
  fields: [
    {
      path: 'buyButton.ctaText',
      kind: 'localized',
      surface: 'theme',
      key: 'ctaText',
      label: { fr: 'Texte du bouton', ar: 'نص الزر', en: 'Button text' },
      hint: { fr: 'Vide = « Ajouter au panier ».', ar: 'اتركه فارغًا ليظهر « أضف إلى السلة ».', en: 'Empty = “Add to cart”.' },
      fallsBackToDefault: true,
    },
    {
      path: 'buyButton.ctaSticky',
      kind: 'toggle',
      surface: 'theme',
      key: 'ctaSticky',
      label: { fr: 'Rester visible au défilement', ar: 'يبقى ظاهرًا عند التمرير', en: 'Stay visible on scroll' },
    },
    {
      path: 'buyButton.ctaShake',
      kind: 'toggle',
      surface: 'theme',
      key: 'ctaShake',
      label: { fr: "Animer le bouton pour attirer l'œil", ar: 'تحريك الزر للفت الانتباه', en: 'Animate the button to catch the eye' },
    },
  ],
};

export const CART_SECTION: EditorSectionDef = {
  key: 'cart',
  group: 'global',
  icon: 'tag',
  label: { fr: 'Panier', ar: 'السلة', en: 'Cart' },
  description: {
    fr: 'Le panneau qui glisse quand on ajoute un produit',
    ar: 'اللوحة التي تظهر عند إضافة منتج',
    en: 'The panel that slides in when a product is added',
  },
  fields: [
    {
      path: 'cart.crossSellHeading',
      kind: 'localized',
      surface: 'settings',
      key: 'cartCrossSellHeading',
      label: {
        fr: 'Titre au-dessus des suggestions',
        ar: 'العنوان فوق الاقتراحات',
        en: 'Heading above the suggestions',
      },
      hint: {
        fr: 'Vide = « Complétez le look ». Cette formule vient d’un thème de vêtements — mettez ce qui correspond à ce que vous vendez, par ex. « Ça pourrait vous intéresser ».',
        ar: 'اتركه فارغًا ليظهر « أكمل إطلالتك ». هذه الصيغة مأخوذة من قالب ملابس — اكتب ما يناسب ما تبيعه، مثل « قد يهمّك أيضًا ».',
        en: 'Empty = “Complete the look”. That phrasing comes from a clothing theme — put whatever fits what you sell, e.g. “You might also like”.',
      },
      fallsBackToDefault: true,
    },
  ],
};

export const GLOBAL_SECTIONS: EditorSectionDef[] = [
  BRAND_COLORS_SECTION,
  TYPOGRAPHY_SECTION,
  BRAND_SECTION,
  BUY_BUTTON_SECTION,
  CART_SECTION,
];
